const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export class ApiError extends Error {
  status: number
  body?: string
  code?: string

  constructor(status: number, message: string, body?: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.code = code
  }
}

export const ACCOUNT_BLOCKED_CODE = 'ACCOUNT_BLOCKED'

export function isAccountBlockedError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === ACCOUNT_BLOCKED_CODE
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { token?: string } = {}
): Promise<T> {
  const { token, ...rest } = options
  if (import.meta.env.DEV && token) {
    // eslint-disable-next-line no-console
    console.warn('apiFetch token provided; prefer cookie auth instead')
  }
  const headers = new Headers(rest.headers)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    credentials: rest.credentials ?? 'include',
    headers,
  })

  if (!res.ok) {
    const text = await res.text()
    let message = text || res.statusText
    let code: string | undefined

    if (text) {
      try {
        const parsed = JSON.parse(text) as {
          message?: string
          error?: { message?: string; code?: string }
        }
        message = parsed.error?.message || parsed.message || message
        code = parsed.error?.code
      } catch {
        // Ignore JSON parse errors and fall back to text.
      }
    }

    throw new ApiError(res.status, message, text, code)
  }

  const text = await res.text()
  if (!text) {
    return null as T
  }

  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(res.status, text || res.statusText, text)
  }
}

export async function apiFetchBlob(
  path: string,
  options: RequestInit & { token?: string } = {}
): Promise<Blob> {
  const { token, ...rest } = options
  if (import.meta.env.DEV && token) {
    // eslint-disable-next-line no-console
    console.warn('apiFetchBlob token provided; prefer cookie auth instead')
  }
  const headers = new Headers(rest.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${API_URL}${path}`, {
    ...rest,
    credentials: rest.credentials ?? 'include',
    headers,
  })

  if (!res.ok) {
    const text = await res.text()
    let message = text || res.statusText
    let code: string | undefined

    if (text) {
      try {
        const parsed = JSON.parse(text) as {
          message?: string
          error?: { message?: string; code?: string }
        }
        message = parsed.error?.message || parsed.message || message
        code = parsed.error?.code
      } catch {
        // Ignore JSON parse errors and fall back to text.
      }
    }

    throw new ApiError(res.status, message, text, code)
  }

  return res.blob()
}
